import apiUser from '@/api/modules/user'
import router from '@/router'

interface StoredUserInfo {
  id: number
  username: string
  name?: string
  phone?: string
  email?: string
  gender?: 0 | 1 | 2
  employment_status?: 1 | 2
  avatar?: string
  remark?: string
  role?: {
    id?: number
    name?: string
  }
}

function getStoredUserInfo(): StoredUserInfo | null {
  try {
    const info = localStorage.getItem('erp_user_info')
    return info ? JSON.parse(info) as StoredUserInfo : null
  }
  catch {
    return null
  }
}

export const useUserStore = defineStore(
  // 唯一ID
  'user',
  () => {
    const settingsStore = useSettingsStore()
    const routeStore = useRouteStore()
    const menuStore = useMenuStore()
    const tabbarStore = useTabbarStore()

    const account = ref(localStorage.getItem('erp_account') ?? '')
    const token = ref(localStorage.getItem('erp_token') ?? '')
    const avatar = ref(localStorage.getItem('erp_avatar') ?? '')
    const permissions = ref<string[]>([])
    const userInfo = ref<StoredUserInfo | null>(getStoredUserInfo())
    let requestLogoutPromise: Promise<void> | null = null

    // 清理旧版刷新逻辑写入的错误键，避免登出后恢复过期登录态。
    localStorage.removeItem('token')

    const userId = computed(() => userInfo.value?.id || null)

    const isLogin = computed(() => {
      if (token.value) {
        return true
      }
      return false
    })

    function setToken(value: string) {
      const normalizedToken = value.replace(/^Bearer\s+/i, '')
      token.value = normalizedToken
      localStorage.removeItem('token')
      if (normalizedToken) {
        localStorage.setItem('erp_token', normalizedToken)
      }
      else {
        localStorage.removeItem('erp_token')
      }
    }

    // 登录
    async function login(data: {
      username: string
      password: string
      remember?: boolean
    }) {
      const res = await apiUser.login(data)
      const { token: userToken, user } = res.data

      localStorage.setItem('erp_account', user.username)
      localStorage.setItem('erp_avatar', user.avatar || '')
      localStorage.setItem('erp_user_info', JSON.stringify(user))

      account.value = user.username
      setToken(userToken)
      avatar.value = user.avatar || ''
      userInfo.value = user as StoredUserInfo
    }

    function logoutCleanStatus() {
      localStorage.removeItem('erp_account')
      localStorage.removeItem('erp_avatar')
      localStorage.removeItem('erp_user_info')
      localStorage.removeItem('userInfo') // 清除可能存在的旧字段
      account.value = ''
      avatar.value = ''
      userInfo.value = null
      permissions.value = []
      settingsStore.updateSettings({}, true)
      tabbarStore.clean()
      routeStore.removeRoutes()
      menuStore.setActived(0)
    }

    async function redirectToLogin(redirect: string, replace: boolean) {
      const loginLocation = {
        name: 'login',
        query: {
          ...(redirect !== settingsStore.settings.home.fullPath && router.currentRoute.value.name !== 'login' && { redirect }),
        },
      }

      setToken('')
      logoutCleanStatus()

      if (router.currentRoute.value.name === 'login') {
        return
      }

      try {
        if (replace) {
          await router.replace(loginLocation)
        }
        else {
          await router.push(loginLocation)
        }
      }
      finally {
        // 路由守卫异常或导航被中止时，使用硬跳转保证失效会话离开业务页。
        if (router.currentRoute.value.name !== 'login') {
          window.location.replace(router.resolve(loginLocation).href)
        }
      }
    }

    // 手动登出
    function logout(redirect = router.currentRoute.value.fullPath) {
      return redirectToLogin(redirect, false)
    }

    // 会话失效时可能有多个并发请求同时触发，只执行一次跳转。
    function requestLogout() {
      if (!requestLogoutPromise) {
        requestLogoutPromise = redirectToLogin(router.currentRoute.value.fullPath, true)
          .finally(() => {
            requestLogoutPromise = null
          })
      }
      return requestLogoutPromise
    }

    // 获取权限
    async function getPermissions() {
      // 调用获取用户信息接口来验证 token 是否有效
      await apiUser.profile()
      // 如果需要权限数据，可以从用户信息中提取
      // permissions.value = res.data.permissions || []
    }
    // 修改密码
    async function editPassword(data: {
      old_password: string
      new_password: string
      new_password_confirmation: string
    }) {
      // 转换字段名以匹配 API 要求
      await apiUser.passwordEdit({
        old_password: data.old_password,
        new_password: data.new_password,
        confirm_password: data.new_password_confirmation,
      })
    }

    // 更新个人信息
    async function updateProfile(id: number, data: any) {
      await apiUser.updateProfile(id, data)
      // 重新获取最新的用户信息
      const profileRes = await apiUser.profile()
      const updatedAdmin = profileRes.data

      // 更新本地存储的用户信息
      localStorage.setItem('erp_account', updatedAdmin.username)
      localStorage.setItem('erp_avatar', updatedAdmin.avatar || '')
      localStorage.setItem('erp_user_info', JSON.stringify(updatedAdmin))

      account.value = updatedAdmin.username
      avatar.value = updatedAdmin.avatar || ''
      userInfo.value = updatedAdmin as StoredUserInfo
    }

    return {
      account,
      token,
      avatar,
      permissions,
      userInfo,
      userId,
      isLogin,
      setToken,
      login,
      logout,
      requestLogout,
      getPermissions,
      editPassword,
      updateProfile,
    }
  },
)
